import React, { useState } from 'react'
import {
  Box,
  TextField,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { useTranslation } from 'react-i18next'
import { mw, aiBtnSx } from '../../views/menuWorkspaceStyles'
import { supportsPhonetic, transliterate } from '../../config/phoneticInput'
import { getLanguageLabel } from '../../config/menuLanguages'

/**
 * One translated-name field, with two ways to fix text an owner's keyboard
 * cannot produce:
 *
 *  - Phonetic typing (Indic scripts): a second input where Latin letters are
 *    converted live into the script and written straight into the field.
 *  - Fix with AI (every language): describe the correction in plain words or a
 *    phonetic respelling and the backend returns the corrected name.
 *
 * The field itself always holds and saves the native-script value, and stays
 * directly editable so backspacing and reordering work without either tool.
 */
export default function AlternateNameField({
  value,
  onChange,
  languageCode,
  fallbackLabel,
  fallbackPlaceholder,
  serverUrl,
}) {
  const { t } = useTranslation()

  const [phoneticOn, setPhoneticOn] = useState(false)
  const [phoneticBuffer, setPhoneticBuffer] = useState('')
  const [fixOpen, setFixOpen] = useState(false)
  const [fixInstruction, setFixInstruction] = useState('')
  const [fixing, setFixing] = useState(false)
  const [fixError, setFixError] = useState('')

  const label = getLanguageLabel(languageCode) || fallbackLabel
  const canPhonetic = supportsPhonetic(languageCode)

  // Typing phonetically drives the real field, so the owner watches the script
  // build up in the value that actually gets saved.
  const handlePhonetic = (latin) => {
    setPhoneticBuffer(latin)
    onChange(transliterate(latin, languageCode))
  }

  // A manual edit makes the phonetic buffer stale — drop it so the next
  // phonetic keystroke doesn't resurrect the pre-edit text.
  const handleDirectEdit = (next) => {
    if (phoneticBuffer) setPhoneticBuffer('')
    onChange(next)
  }

  const handleFix = async () => {
    if (!fixInstruction.trim()) return
    setFixing(true)
    setFixError('')
    try {
      const res = await fetch(`${serverUrl}/ai/fix-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current: value || '',
          instruction: fixInstruction.trim(),
          language: languageCode,
        }),
      })
      const data = await res.json()
      if (!res.ok || !data.text) {
        setFixError(data.error || t('Could not fix that. Try describing it differently.'))
      } else {
        onChange(data.text)
        setPhoneticBuffer('')
        setFixInstruction('')
        setFixOpen(false)
      }
    } catch (err) {
      setFixError(err.message)
    }
    setFixing(false)
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 32 }}>
        <Typography variant="caption" sx={{ color: mw.color.textSecondary, fontWeight: 700, flex: 1 }}>
          {label}
        </Typography>

        {canPhonetic && (
          <FormControlLabel
            sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.72rem', color: mw.color.textMuted } }}
            control={
              <Switch
                size="small"
                checked={phoneticOn}
                onChange={(e) => {
                  setPhoneticOn(e.target.checked)
                  if (!e.target.checked) setPhoneticBuffer('')
                }}
              />
            }
            label={t('Type in English letters')}
          />
        )}

        {languageCode && (
          <Tooltip title={t('Describe the correction and AI rewrites it in {{lang}}', { lang: label })}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => { setFixOpen((o) => !o); setFixError('') }}
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
              sx={{ ...aiBtnSx, fontSize: '0.7rem', px: 1.25, py: 0.25 }}
            >
              {t('Fix with AI')}
            </Button>
          </Tooltip>
        )}
      </Box>

      <TextField
        fullWidth
        value={value}
        onChange={(e) => handleDirectEdit(e.target.value)}
        margin="dense"
        size="small"
        placeholder={languageCode ? t('{{lang}} name', { lang: label }) : fallbackPlaceholder}
      />

      {canPhonetic && phoneticOn && (
        <TextField
          fullWidth
          value={phoneticBuffer}
          onChange={(e) => handlePhonetic(e.target.value)}
          margin="dense"
          size="small"
          label={t('Type in English letters')}
          placeholder="chikan biryani"
          helperText={t('Converts as you type. Capitals give the hard sounds: T, D, N, L, S.')}
          FormHelperTextProps={{ sx: { fontSize: '0.68rem', color: mw.color.textMuted } }}
        />
      )}

      {fixOpen && languageCode && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: mw.radius.sm,
            bgcolor: mw.color.aiSoft,
            border: `1px solid ${mw.color.aiBorder}`,
          }}
        >
          <Typography variant="caption" sx={{ color: mw.color.textSecondary, display: 'block', mb: 0.75 }}>
            {t("What's wrong with it? Spell it how it should sound, or just describe the problem.")}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              autoFocus
              size="small"
              value={fixInstruction}
              onChange={(e) => setFixInstruction(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleFix() } }}
              placeholder={t("e.g. the 'mer' part should sound like 'maare'")}
              sx={{ bgcolor: '#fff', borderRadius: mw.radius.sm }}
            />
            <Button
              variant="outlined"
              onClick={handleFix}
              disabled={fixing || !fixInstruction.trim()}
              startIcon={fixing ? <CircularProgress size={12} /> : null}
              sx={{ ...aiBtnSx, whiteSpace: 'nowrap' }}
            >
              {t('Apply')}
            </Button>
          </Box>
          {fixError && (
            <Typography variant="caption" sx={{ color: mw.color.off, display: 'block', mt: 0.75 }}>
              {fixError}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}
